import {
  ascend,
  sortWith,
  prop,
  propEq,
  keys,
  values,
  isEmpty,
} from 'ramda';

import { TABLES_SORTING_TYPE_CONFIG } from '@ezugi/lobby-core/constants/sorting';
import { sortTablesByGameId } from '@ezugi/lobby-core/sorting/sortTablesByGameId';
import { sortTablesByLanguage } from '@ezugi/lobby-core/sorting/sortTablesByLanguage';
import { sortTablesByLimits } from '@ezugi/lobby-core/sorting/sortTablesByLimits';
import { toLobbyTables, toLobbyTableIds } from '@ezugi/lobby-core/tables/tableAdapters';

import { isBJNormalSeats } from './functions';

/*
    Class containing util functions to sort tables
*/

// sort tables based on activity state, actives first
// this is the last sorting before displaying since closed tables are always last
// closed tables with open hours comes before closed tables without open hours
// group_tables = array of table ids to sort
// tables = all tables data from reducer
export const sortClosedTables = (groupTables, tables) => groupTables.slice().sort((tableId1, tableId2) => {
  // 1 open and 1 closed
  if (tables[tableId1].active && !tables[tableId2].active) return -1;
  if (!tables[tableId1].active && tables[tableId2].active) return 1;

  // both closed
  if (!tables[tableId1].active && !tables[tableId2].active) {
    // 1 with open hours and 1 without
    if (
      tables[tableId1].openHour != null
        && tables[tableId2].openHour == null
    ) return -1;
    if (
      tables[tableId1].openHour == null
        && tables[tableId2].openHour != null
    ) return 1;

    // both with open hours
    // convert to date object so it can be compared
    if (
      tables[tableId1].openHour != null
        && tables[tableId2].openHour != null
    ) {
      const d1 = new Date();
      const openSplit1 = tables[tableId1].openHour.split(':');
      d1.setHours(openSplit1[0]);
      d1.setMinutes(openSplit1[1]);

      const d2 = new Date();
      const openSplit2 = tables[tableId2].openHour.split(':');
      d2.setHours(openSplit2[0]);
      d2.setMinutes(openSplit2[1]);

      if (d1.getTime() < d2.getTime()) return -1;
      if (d1.getTime() > d2.getTime()) return 1;
    }
  }

  return 0;
});

// return array of sorted limits of specific table (only ids)
// receive limits as object, key is the limit id
export const sortTableLimits = (limitsObj) => {
  const limitIds = keys(limitsObj);
  if (limitIds.length < 2) return limitIds;
  limitIds.sort((id1, id2) => {
    if (limitsObj[id1].Min_Bet < limitsObj[id2].Min_Bet) return -1;
    if (limitsObj[id1].Min_Bet > limitsObj[id2].Min_Bet) return 1;

    if (limitsObj[id1].Max_Bet < limitsObj[id2].Max_Bet) return -1;
    if (limitsObj[id1].Max_Bet > limitsObj[id2].Max_Bet) return 1;
    return 0;
  });
  return limitIds;
};

// sort tables by favorite
// group_tables = array of table ids to sort
// tables = all tables data from reducer
// favorite = array of favorite tables
export const sortTablesByFavorite = (groupTables, favorite) => groupTables.slice().sort((tableId1, tableId2) => {
  // find current table id in favorite list
  const table1IsFavorite = favorite.includes(tableId1);
  const table2IsFavorite = favorite.includes(tableId2);

  if (table1IsFavorite && !table2IsFavorite) return -1;
  if (!table1IsFavorite && table2IsFavorite) return 1;
  return 0;
});

// sort tables by recent
// group_tables = array of table ids to sort
// recent = array of favorite tables
export const sortTablesByRecent = (groupTables, recent) => sortWith([
  ascend((tableId) => recent.includes(tableId)) ])(groupTables);

// create a list of open tables and closed tables
// since Array.sort might re order items that are considered equal, we have to sort the active tables separately from the inactive ones
const separateTables = (tableIds, tables) => {
  // TODO: evaluate if this should be added
  const existingTables = tableIds.filter((id) => tables[id]);

  const openTables = existingTables.filter((id) => tables[id].active);
  const closedTables = existingTables.filter((id) => !tables[id].active);

  return { openTables, closedTables };
};

// general sort tables in group, use sort type (by config) and tables list to add tables ids to this group tables list
export const sortTables = (
  groupTables,
  tables,
  sortType,
  language,
  filters,
  groupId
) => {
  let { openTables, closedTables } = separateTables(groupTables, tables);

  // eslint-disable-next-line eqeqeq
  const group = values(filters.game_groups).find((gameGroup) => gameGroup.id == groupId);

  switch (sortType) {
  case TABLES_SORTING_TYPE_CONFIG.LIMITS: {
    openTables = toLobbyTableIds(sortTablesByLimits(toLobbyTables(openTables, tables)));
    break;
  }
  case TABLES_SORTING_TYPE_CONFIG.LANGUAGE: {
    openTables = toLobbyTableIds(sortTablesByLanguage({
      groupLiveTables: toLobbyTables(openTables, tables),
      groupConfig: group,
      language,
    }));
    break;
  }
  case TABLES_SORTING_TYPE_CONFIG.DEFAULT: {
    openTables = toLobbyTableIds(sortTablesByGameId({
      groupLiveTables: toLobbyTables(openTables, tables),
      groupConfig: group,
    }));
    break;
  }
  default:
    break;
  }
  // sort by activity state (always last!)
  closedTables = sortClosedTables(closedTables, tables);

  return openTables.concat(closedTables);
};
// override sort tables function of group data
// display favorite first and than recent
// group_tables = array of table ids to sort
// tables = all tables data from reducer
// favorite = array of favorite tables
// recent = array of recent tables
export const sortTablesMyLobby = (groupTables, tables, favorite, recent) => {
  // Send all recent to start
  const sortedByRecent = sortTablesByRecent(groupTables, recent);

  // Send all favorite to start
  const sortedByFavorite = sortTablesByFavorite(sortedByRecent, favorite);

  // Sort by activity state (always last!)
  return sortClosedTables(sortedByFavorite, tables);
};

const isFullSeatedTable = (table) => {
  const hasEmptySeat = table.history.some((seat) => !seat.Taken);

  // If history is empty, we assume the table is not full
  if (isEmpty(table.history)) return false;

  return !hasEmptySeat;
};

export const sortFullSeatedBJTables = sortWith([
  ascend(propEq('active', false)),
  ascend(
    (table) => isBJNormalSeats(prop('gameId', table)) && isFullSeatedTable(table)
  ),
]);

export const sortGroupTables = ({
  groupId,
  groupTables,
  tables,
  favorite,
  recent,
  language,
  sortType,
  filters,
  bjSortConfig,
}) => {
  if (+groupId === 0) return sortTablesMyLobby(groupTables, tables, favorite, recent);

  const currentGroup = values(filters?.game_groups).find(
    (group) => group.id === +groupId
  );

  const sortedTables = sortTables(
    groupTables,
    tables,
    sortType,
    language.code,
    filters,
    groupId
  );

  if (bjSortConfig.enableSort && currentGroup.name === bjSortConfig.groupName) {
    return sortFullSeatedBJTables(
      sortedTables.reduce((acc, curr) => [ ...acc, tables[curr] ], [])
    ).map((table) => +table.tableId);
  }

  return sortedTables.map(Number);
};
